/**
 * Wizard 2 Flow Controller
 * 11 quiz steps → 1 plan overview → 1 profile step
 * Trial-only cart; post-order converts trial → full plan
 */

/**
 * Checkout discount codes (Wizard 2 trial checkout).
 * - Order discount: creator/session override, else defaultOrderDiscount (applied to cart, not URL)
 * - Shipping: combined with order discount on cart via /cart/update.js
 */
window.__wizard2CheckoutDiscountCodes = {
  defaultOrderDiscount: 'TRANSITION',
  shipping: 'TRANSITIONSHIPPING',
  storageKey: 'wizard2_creator_product_discount',
  /** Applied to plan overview trial product line: final = base × (1 − pct/100). Must match TRANSITION code in Shopify. */
  trialOrderDiscountPercent: 25,
  /** Referral/creator product discounts apply to the trial product line in Shopify checkout. */
  referralTrialOrderDiscountPercent: 40,
  /** Total effective discount % shown in offer bar before plan overview in referral/creator mode. */
  referralOfferBarPercentBeforePlanOverview: 50
};

/**
 * Store creator/partner order discount code from landing URL for checkout (sessionStorage).
 * Prefer: ?creator_discount=eva30 or ?creator=eva30
 * Also supports: ?snowball=eva30 (referral cards)
 * Also: ?discount=eva30 when not the default order discount (TRANSITION) or shipping code alone.
 */
function captureWizard2CreatorDiscountFromUrl() {
  const cfg = window.__wizard2CheckoutDiscountCodes;
  if (!cfg) return;
  const def = String(cfg.defaultOrderDiscount || '').toLowerCase();
  const ship = String(cfg.shipping || '').toLowerCase();
  const storageKey = cfg.storageKey;
  try {
    const params = new URLSearchParams(window.location.search);
    const explicit = params.get('creator_discount') || params.get('creator');
    const snowball = params.get('snowball');
    const discountParam = params.get('discount');
    const discountFirst = discountParam
      ? String(discountParam).split(',')[0].trim().toLowerCase()
      : '';

    const normalizedExplicit = explicit != null ? String(explicit).trim().toLowerCase() : '';
    const normalizedSnowball = snowball != null ? String(snowball).trim().toLowerCase() : '';

    // One source wins, in priority order:
    // 1) creator_discount / creator
    // 2) snowball
    // 3) discount (first code when comma-separated)
    const code =
      normalizedExplicit ||
      normalizedSnowball ||
      discountFirst;

    if (!code) return;

    if (code === def || code === ship) {
      sessionStorage.removeItem(storageKey);
      return;
    }

    sessionStorage.setItem(storageKey, code);
  } catch (e) {
    /* sessionStorage unavailable */
  }
}

captureWizard2CreatorDiscountFromUrl();

/** Quiz steps only (not plan_overview / profile) — reset window scroll so offer bar stays visible */
const WIZARD_2_QUIZ_STEPS_SCROLL_TOP = new Set([
  'get_started',
  'name',
  'spayed',
  'age',
  'breed',
  'weight',
  'composition',
  'activity',
  'allergies',
  'snacks',
  'email',
  'calculation'
]);

class Wizard2Flow {
  constructor() {
    this.config = null;
    this.router = null;
    this.state = null;
    this.storage = null;
    this.shopifyAPI = null;
    this.initialized = false;
  }

  async init() {
    if (this.initialized) return;

    captureWizard2CreatorDiscountFromUrl();

    try {
      const config = await flowConfig.getFlowConfig('wizard-2');
      this.config = config;

      if (typeof PetFlowState === 'undefined' || typeof PetFlowStorage === 'undefined' ||
          typeof FlowRouter === 'undefined' || typeof ShopifyAPIWizard2 === 'undefined') {
        throw new Error('Required component files not loaded');
      }

      this.state = new PetFlowState('wizard-2');
      this.storage = new PetFlowStorage('wizard-2');
      this.router = new FlowRouter(config);
      this.shopifyAPI = new ShopifyAPIWizard2(config);

      if (typeof CalculationEngine === 'undefined') {
        throw new Error('wizard-1-calculations.js required');
      }
      this.calculationEngine = new CalculationEngine();

      if (typeof StepHandlerWizard2 === 'undefined') {
        throw new Error('wizard-2-steps.js required');
      }
      this.stepHandler = new StepHandlerWizard2(this);
      await this.stepHandler.init();
      if (typeof this.stepHandler.ensureWizard2ShippingRatesLoaded === 'function') {
        await this.stepHandler.ensureWizard2ShippingRatesLoaded();
      }

      window.addEventListener('pageshow', e => {
        if (!e.persisted) return;
        if (typeof this.stepHandler?.resetNextButtonAfterBfcacheRestore === 'function') {
          this.stepHandler.resetNextButtonAfterBfcacheRestore();
        }
      });

      if (typeof Wizard2Analytics !== 'undefined') {
        this.analytics = new Wizard2Analytics(this);
      } else {
        this.analytics = null;
      }

      const backendApiUrl = window.__wizard2BackendApiUrl;
      if (typeof KlaviyoEventManagerWizard2 !== 'undefined' && backendApiUrl) {
        this.klaviyoManager = new KlaviyoEventManagerWizard2(backendApiUrl, config);
      } else {
        this.klaviyoManager = null;
      }

      this.router.onRouteChange((step, params) => {
        this.handleRouteChange(step, params);
      });

      await this.detectLoggedInCustomer();
      await this.loadSavedState();
      this.applyLoggedInCustomerPrefill();

      window.wizard2Flow = this;

      const path = this.router.getCurrentPath();
      const stepFromPath = this.router.getStepFromPath(path);
      if (stepFromPath === 'get_started' && this.hasQuizProgress()) {
        const resumeStep = this.state.getFurthestValidStep() || 'name';
        const targetStep = resumeStep === 'get_started' ? 'name' : resumeStep;
        this.router.clearCameFromGetStarted();
        this.router.replaceUrlForStep(targetStep);
        this.state.setCurrentStep(targetStep);
      } else if (this.router.isRootUrl(path, stepFromPath)) {
        const petData = this.state.getPetData();
        const hasLoadedData = petData.name || petData.gender || petData.ageGroup || petData.breed ||
          petData.weight !== null || this.state.customerId;
        if (hasLoadedData) {
          const resumeStep = this.state.getCurrentStep() || 'name';
          const targetStep = this.router.getPathForStep(resumeStep) ? resumeStep : 'name';
          this.router.replaceUrlForStep(targetStep);
          this.state.setCurrentStep(targetStep);
        } else {
          this.state.setCurrentStep('name');
          this.router.replaceUrlForStep('name');
        }
      }

      this.router.init();
      this.setupAutoSave();

      this.initialized = true;
      this.updatePageTitle();
      console.log('Wizard 2 Flow initialized');
    } catch (error) {
      console.error('Error initializing Wizard 2 flow:', error);
      throw error;
    }
  }

  async detectLoggedInCustomer() {
    try {
      const query = `query { customer { id email firstName } }`;
      const data = await this.shopifyAPI.executeGraphQL(query);
      if (data?.customer?.id) {
        this.state.customerId = data.customer.id;
      }
      this._loggedInCustomerEmail = (data?.customer?.email && String(data.customer.email).trim()) || null;
      this._loggedInCustomerFirstName = (data?.customer?.firstName && String(data.customer.firstName).trim()) || null;
    } catch (e) {
      this._loggedInCustomerEmail = null;
      this._loggedInCustomerFirstName = null;
      // Anonymous user - expected
    }
  }

  /**
   * Prefill email step when Shopify customer is logged in (and session second-dog preserves session email).
   */
  applyLoggedInCustomerPrefill() {
    const email = this._loggedInCustomerEmail;
    const firstName = this._loggedInCustomerFirstName;
    const p = this.state.petData;
    if (email && (!p.ownerEmail || !String(p.ownerEmail).trim())) {
      this.state.updatePetData('ownerEmail', email);
    }
    if (firstName && (!p.ownerName || !String(p.ownerName).trim())) {
      this.state.updatePetData('ownerName', firstName);
    }
  }

  async loadSavedState() {
    const urlParams = new URLSearchParams(window.location.search);
    const petIds = urlParams.getAll('pet');
    if (petIds.length > 0 && this.shopifyAPI) {
      try {
        const currentPetId = petIds[petIds.length - 1];
        const metaobject = await this.shopifyAPI.getPetMetaobjectById(currentPetId);
        if (metaobject) {
          this.stepHandler.loadPetDataFromMetaobject(metaobject);
          if (metaobject.cartItems?.length > 0) {
            try {
              await this.shopifyAPI.addToCart(metaobject.cartItems);
            } catch (e) {}
          }
          if (typeof this.stepHandler?.updateWizard2OfferBarForPlanOverview === 'function') {
            this.stepHandler.updateWizard2OfferBarForPlanOverview();
          }
          return;
        }
      } catch (e) {}
    }

    if (this.state.customerId) {
      try {
        const shopifyPets = await this.shopifyAPI.getAllPetMetaobjects(this.state.customerId);
        if (shopifyPets?.length > 0) {
          const metaobject = await this.shopifyAPI.getPetMetaobjectById(shopifyPets[0].id);
          if (metaobject) {
            this.stepHandler.loadPetDataFromMetaobject(metaobject);
            if (typeof this.stepHandler?.updateWizard2OfferBarForPlanOverview === 'function') {
              this.stepHandler.updateWizard2OfferBarForPlanOverview();
            }
            return;
          }
        }
      } catch (e) {}
    }

    const savedState = this.storage.loadFromLocalStorage();
    if (savedState) {
      const currentCountry = window.__wizard2CurrentCountry || '';
      const savedCountry = savedState._savedCountry || '';
      if (savedCountry && currentCountry && savedCountry.toUpperCase() !== currentCountry.toUpperCase()) {
        delete savedState.variantPricing;
        delete savedState.variantPricingHalf;
        delete savedState.presentmentCurrencyCode;
      }
      delete savedState._savedCountry;
      this.state.loadState(savedState);
    }

    if (typeof this.stepHandler?.updateWizard2OfferBarForPlanOverview === 'function') {
      this.stepHandler.updateWizard2OfferBarForPlanOverview();
    }

    if (this.klaviyoManager && this.state.petData?.ownerEmail) {
      try {
        this.klaviyoManager.resumePendingEvent(this.state.petData.ownerEmail);
      } catch (e) {}
    }
  }

  setupAutoSave() {}

  /**
   * After navigating to a quiz step, scroll the window to top so header + offer bar are visible.
   * Scroll only; does not move focus. Skips plan_overview and profile.
   * @param {string} step
   */
  scrollWindowToTopIfQuizStep(step) {
    if (!WIZARD_2_QUIZ_STEPS_SCROLL_TOP.has(step)) return;
    const scrollTop = () => {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      } catch (e) {
        window.scrollTo(0, 0);
      }
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollTop();
        setTimeout(scrollTop, 0);
      });
    });
  }

  async handleRouteChange(step, params) {
    if (this.klaviyoManager && this.router) {
      this.klaviyoManager.setLastOriginPath(window.location.origin, window.location.pathname);
    }

    if (step === 'plan_onetime') {
      if (!window.Wizard2OnetimePage?.hasPlanData(this)) {
        const calcs = this.state.calculations || {};
        const hasQuiz = !!(this.state.getPetData?.()?.name && calcs.dailyFoodG != null);
        this.router.navigate(hasQuiz ? 'plan_overview' : 'name', {}, true);
        return;
      }
    }

    this.state.setCurrentStep(step);
    if (step === 'get_started') this.router.markCameFromGetStarted();

    const planFooter = document.querySelector('[data-plan-price-footer]');
    const footerCheckmarks = document.querySelector('[data-wizard-2-footer-checkmarks]');
    if (step === 'plan_overview') {
      if (planFooter) planFooter.style.display = '';
      if (footerCheckmarks) footerCheckmarks.style.display = 'flex';
    } else {
      if (planFooter) planFooter.style.display = 'none';
      if (footerCheckmarks) footerCheckmarks.style.display = 'none';
    }

    await this.renderStep(step, params);

    if (typeof this.stepHandler?.updateWizard2OfferBarForPlanOverview === 'function') {
      this.stepHandler.updateWizard2OfferBarForPlanOverview();
    }

    this.scrollWindowToTopIfQuizStep(step);

    if (this.analytics?.trackStepView) {
      try { this.analytics.trackStepView(step); } catch (e) {}
    }

    const state = this.state.getState();
    if (step === 'get_started') state.currentStep = 'name';
    const hasData = state.petData.name || state.petData.gender || state.customerId ||
      state.petMetaobjectId || state.petData.ageGroup || state.petData.breed || state.petData.weight !== null;
    if (hasData || this.initialized) {
      await this.storage.autoSave(state, this.state.customerId);
    }
  }

  hasQuizProgress() {
    const pet = this.state?.getPetData?.() || {};
    return !!(pet.name || pet.gender || pet.ageGroup || pet.breed ||
      pet.weight != null || this.state?.petMetaobjectId);
  }

  async saveBeforeStepCompletion() {
    const state = this.state.getState();
    const currentStep = this.state.getCurrentStep();
    const stepOrder = ['name', 'spayed', 'age', 'breed', 'weight', 'composition', 'activity', 'allergies', 'snacks', 'email', 'calculation', 'plan_overview', 'profile'];
    const nextIndex = stepOrder.indexOf(currentStep) + 1;
    const nextStep = nextIndex < stepOrder.length ? stepOrder[nextIndex] : null;

    if (currentStep === 'name' && state.petData?.name && !state.petMetaobjectId && this.shopifyAPI?.backendApiUrl) {
      try {
        const result = await this.shopifyAPI.createOrUpdatePetMetaobject({
          petData: state.petData,
          calculations: state.calculations || {},
          resumeStep: 'spayed',
          selectedFlavors: state.selectedFlavorsFull?.length ? state.selectedFlavorsFull : state.selectedFlavorsTrial
        });
        if (result?.petMetaobjectId) {
          this.state.petMetaobjectId = result.petMetaobjectId;
        }
      } catch (e) {}
    }

    await this.storage.autoSave(state, this.state.customerId, {
      forceShopifySave: true,
      nextStep,
      currentStep
    });
  }

  async renderStep(step, params = {}) {
    if (this.stepHandler) {
      await this.stepHandler.renderStep(step, params);
    }
  }

  async navigateNext() {
    const currentStep = this.state.getCurrentStep();
    if (currentStep === 'get_started') {
      this.router.navigateNext(currentStep);
      this.updatePageTitle();
      return;
    }
    if (currentStep === 'plan_overview') {
      if (this.stepHandler && typeof this.stepHandler.handleAddToCart === 'function') {
        await this.stepHandler.handleAddToCart();
      }
      return;
    }
    if (currentStep === 'plan_onetime') {
      const btn = document.querySelector('[data-next-button]');
      if (btn && !btn.disabled && window.Wizard2OnetimePage) {
        await window.Wizard2OnetimePage.checkout(this.stepHandler, btn);
      }
      return;
    }
    if (!this.state.validateStep(currentStep)) return;
    if (!this.state._justCreated) {
      await this.saveBeforeStepCompletion();
    }
    this.state._justCreated = false;
    this.router.navigateNext(currentStep);
    this.updatePageTitle();
  }

  navigatePrevious() {
    this.router.navigatePrevious(this.state.getCurrentStep());
  }

  updatePetData(field, value) {
    this.state.updatePetData(field, value);
    this.storage.autoSave(this.state.getState(), this.state.customerId);
    if (this.klaviyoManager && this.state.petData?.ownerEmail) {
      try {
        this.klaviyoManager.updatePendingEvent(this.state.petData, this.state.petData.ownerEmail);
      } catch (e) {}
    }
  }

  getState() {
    return this.state.getState();
  }

  getConfig() {
    return this.config;
  }

  runCalculations() {
    const petData = this.state.getPetData();
    const results = this.calculationEngine.calculate(petData);
    this.state.updateCalculations(results);
    this.storage.autoSave(this.state.getState(), this.state.customerId);
    return results;
  }

  updatePageTitle() {
    setTimeout(() => {
      const petData = this.state.getPetData();
      const petName = petData?.name?.trim();
      const prefix = petName || (document.querySelector('[data-translate="quiz.title.quiz_steps"]')?.textContent || 'Your dog');
      document.title = `${prefix} 🩵 Blue Zone`;
    }, 0);
  }
}

window.Wizard2Flow = Wizard2Flow;

window.initWizard2Flow = function() {
  if (window.wizard2Flow) return window.wizard2Flow;
  window.wizard2Flow = new Wizard2Flow();
  window.wizard2Flow.init();
  return window.wizard2Flow;
};
